// src/services/analytics.ts
import { User } from 'firebase/auth';

// Define types for our events
export type EventName = 
  // Page events
  | 'page_view'
  // Auth events
  | 'sign_in'
  | 'sign_out'
  // Decision events
  | 'decision_created'
  | 'decision_viewed' 
  | 'decision_shared'
  | 'decision_closed'
  // Participant events
  | 'participant_joined'
  | 'message_sent'
  | 'chat_completed'
  // Response events
  | 'response_viewed'
  | 'response_shared'
  | 'response_copied'
  // Other events
  | 'share_initiated'
  | 'continue_discussion';

// Define interface for event properties
export interface EventProperties {
  [key: string]: any;
}

// Make TypeScript recognize window.amplitude
declare global {
  interface Window {
    amplitude: {
      init: (apiKey: string, options?: any) => void;
      add: (plugin: any) => void;
      track: (eventName: string, eventProperties?: EventProperties) => void;
      setUserId: (userId: string | null) => void;
      Identify: new () => any;
      identify: (identify: any) => void;
      reset: () => void;
    };
    sessionReplay: {
      plugin: (options: any) => any;
    };
  }
}

// Check if Amplitude is available
const isAmplitudeAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' && 
    typeof window.amplitude !== 'undefined' && 
    typeof window.amplitude.track === 'function'
  );
};

// Set user ID for analytics
export const identifyUser = (user: User | null): void => {
  if (!isAmplitudeAvailable()) return;

  if (user) {
    // Set the user ID
    window.amplitude.setUserId(user.uid);
    
    // Set user properties using the Identify pattern
    const identify = new window.amplitude.Identify();
    identify.set('email', user.email);
    identify.set('displayName', user.displayName);
    identify.set('authProvider', user.providerData[0]?.providerId || 'anonymous');
    identify.set('createdAt', user.metadata.creationTime);
    
    window.amplitude.identify(identify);
  } else {
    // Reset user identification when signed out
    window.amplitude.setUserId(null);
    window.amplitude.reset();
  }
};

// Track an event
export const trackEvent = (
  eventName: EventName, 
  eventProperties: EventProperties = {}
): void => {
  if (!isAmplitudeAvailable()) return;
  
  try {
    window.amplitude.track(eventName, eventProperties);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Specific event tracking functions
export const trackSignIn = (method: 'google' | 'anonymous'): void => {
  trackEvent('sign_in', { method });
};

export const trackSignOut = (): void => {
  trackEvent('sign_out');
};

export const trackDecisionCreated = (decisionId: string, question: string, requiresAuth: boolean, visibility: 'public' | 'unlisted'): void => {
  trackEvent('decision_created', { 
    decisionId, 
    questionLength: question.length,
    requiresAuth,
    visibility
  });
};

export const trackDecisionViewed = (decisionId: string): void => {
  trackEvent('decision_viewed', { decisionId });
};

export const trackDecisionShared = (decisionId: string, method: 'copy' | 'email' | 'native' | 'gmail'): void => {
  trackEvent('decision_shared', { decisionId, method });
};

export const trackDecisionClosed = (decisionId: string, participantCount: number): void => {
  trackEvent('decision_closed', { decisionId, participantCount });
};

export const trackParticipantJoined = (
  decisionId: string, 
  authType: 'anonymous' | 'google'
): void => {
  trackEvent('participant_joined', { decisionId, authType });
};

export const trackMessageSent = (
  decisionId: string, 
  participantId: string,
  messageLength: number
): void => {
  trackEvent('message_sent', { 
    decisionId, 
    participantId,
    messageLength 
  });
};

export const trackChatCompleted = (
  decisionId: string, 
  participantId: string,
  messageCount: number
): void => {
  trackEvent('chat_completed', { 
    decisionId, 
    participantId,
    messageCount 
  });
};

export const trackResponseViewed = (
  decisionId: string, 
  responseType: 'interim' | 'final',
  participantCount: number
): void => {
  trackEvent('response_viewed', { 
    decisionId, 
    responseType,
    participantCount 
  });
};

export const trackResponseShared = (
  decisionId: string, 
  method: 'copy' | 'link'
): void => {
  trackEvent('response_shared', { 
    decisionId, 
    method
  });
};

// Default export
const Analytics = {
  identifyUser,
  trackEvent,
  trackSignIn,
  trackSignOut,
  trackDecisionCreated,
  trackDecisionViewed,
  trackDecisionShared,
  trackDecisionClosed,
  trackParticipantJoined,
  trackMessageSent,
  trackChatCompleted,
  trackResponseViewed,
  trackResponseShared
};

export default Analytics;